import AdvancedDataTable from 'Components/Generic/AdvancedDataTable';
import DataTableHeaderTextFieldFilter from 'Components/Generic/AdvancedDataTable/DataTableHeaderTextFieldFilter';
import {
  TEXT_FILTER_MAP,
  TEXT_LIKE_FILTER_MAP,
} from 'Components/Generic/AdvancedDataTable/Helper';
import OrganizationMultiselect from 'Components/Generic/BOSMultiselect/OrganizationMultiselect';
import BOSSelect from 'Components/Generic/BOSSelect';
import QuickViewButton from 'Components/Generic/Buttons/QuickViewButton';
import StatusButton from 'Components/Generic/Buttons/StatusButton';
import OrgNameWithDrawerButton from 'Components/SiteDrawerContainer/OrgNameWithDrawerButton';
import UserNameWithDrawerButton from 'Components/SiteDrawerContainer/UserNameWithDrawerButton';
import { getUserNameFromId } from 'Data/context/Users';
import { useViewportSizer } from 'Data/context/ViewportSizer';
import { getObjectStatus, OBJECT_STATUS } from 'Data/Enum';
import SiteDrawerData from 'Data/SiteDrawerData';
import UserData from 'Data/UserData';
import DataManager from 'Lib/DataManager';
import { usePersistentFiltering } from 'Lib/hooks/usePersistentFiltering';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';

let DM = new DataManager();

let filterOptions = {
  full_name: {
    seqRestKey: '*full_name',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  username: {
    seqRestKey: '*username',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  notes: {
    seqRestKey: '*notes',
    mapToSeqRestValue: TEXT_LIKE_FILTER_MAP,
  },
  organizations: {
    seqRestKey: '*$organizations.id',
  },
  status: {
    seqRestKey: '*status',
  },
};

const UserListView = ({ addNewUserButton }) => {
  const { narrowScreen } = useViewportSizer();

  const [data, setData] = useState();
  const [refreshKey, setRefreshKey] = useState(0);
  const [persistentFilter, updateFilterField] =
    usePersistentFiltering(`USER_FILTER`);

  const runUserQuery = (query) => {
    UserData.getRecordList({
      ...query,
      _include: 'organizations',
    });
  };

  useEffect(() => {
    DM.add(UserData.registerListener('LIST_LOADED', setData), UserData);
    DM.add(
      UserData.registerListener('LIST_LOAD_ERROR', onUserListLoadError),
      UserData,
    );
    DM.add(UserData.registerListener('RECORD_CREATED', refreshData), UserData);
    DM.add(UserData.registerListener('RECORD_UPDATED', refreshData), UserData);
    DM.add(
      UserData.registerListener('RECORD_ACTIVATED', refreshAndCloseDrawer),
      UserData,
    );
    DM.add(
      UserData.registerListener('RECORD_DEACTIVATED', refreshAndCloseDrawer),
      UserData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  const onUserListLoadError = () => {};

  const refreshData = () => {
    setRefreshKey((prevState) => prevState + 1);
  };
  const refreshAndCloseDrawer = () => {
    refreshData();
    SiteDrawerData.close();
  };

  const headers = [
    {
      label: 'Name',
      value: 'full_name',
      width: 160,
      sortable: true,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Name'}
            defaultValue={persistentFilter?.full_name}
            onFilterValueUpdate={(e) =>
              updateFilterField('full_name', e.target.value)
            }
          />
        );
      },
      render(user) {
        return <Link to={`./${user.id}`}>{user.full_name}</Link>;
      },
      getText: (user) => {
        return getUserNameFromId(user.id);
      },
    },

    {
      label: 'UNID',
      value: 'username',
      width: 120,
      sortable: true,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'User ID'}
            defaultValue={persistentFilter?.username}
            onFilterValueUpdate={(e) =>
              updateFilterField('username', e.target.value)
            }
          />
        );
      },
    },

    {
      label: 'Email',
      value: 'emails',
      width: 200,
      sortable: true,
      format: (email) => (
        <a href={`mailto:${email}`} onMouseDown={(e) => e.preventDefault()}>
          {email}
        </a>
      ),
      getText: (user) => user.email,
    },

    // {
    //   label: 'Position',
    //   value: 'job_title',
    //   width: 100,
    //   sortable: true,
    // },

    {
      label: 'Office Phone',
      value: 'office_phone',
      width: 140,
      fieldName: 'phone_numbers',
      format: (phone_json) => {
        return phone_json?.office;
      },
    },

    {
      label: narrowScreen ? 'Core(s)' : 'Core Affiliation(s)',
      value: 'organizations',
      width: 200,
      filterable: true,
      renderHeader() {
        return (
          <OrganizationMultiselect
            onSelectedOrganizationsUpdate={(orgList) =>
              updateFilterField('organizations', orgList)
            }
            defaultSelectedItems={persistentFilter?.organizations}
            isHeader
          />
        );
      },
      format: (orgs) => {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              paddingTop: 4,
              paddingBottom: 4,
            }}>
            {orgs
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((org, index) => (
                <OrgNameWithDrawerButton key={index} orgId={org.id} />
              ))}
          </div>
        );
      },
      getText: (user) =>
        user.organizations
          ? user.organizations
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((org) => org.name)
              .join(', ')
          : '',
    },

    {
      label: 'Notes',
      value: 'notes',
      // width: 200,
      sortable: true,
      filterable: true,
      truncated: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Notes'}
            defaultValue={persistentFilter?.notes}
            onFilterValueUpdate={(e) =>
              updateFilterField('notes', e.target.value)
            }
          />
        );
      },
    },

    {
      label: 'Status',
      value: 'status',
      width: 120,
      centeredHeader: true,
      filterable: true,
      renderHeader() {
        return (
          <BOSSelect
            listData={Object.values(OBJECT_STATUS)}
            idField={'key'}
            labelField={'label'}
            labelText='Status'
            onItemSelect={(item) => updateFilterField('status', [item])}
            defaultSelectedId={
              persistentFilter?.status?.length
                ? persistentFilter.status[0].key
                : undefined
            }
            isHeader
          />
        );
      },
      format: (status) => {
        return (
          <StatusButton
            statusKey={status}
            icon={
              status === OBJECT_STATUS.DEACTIVATED.key
                ? 'blocked-person'
                : 'person'
            }
          />
        );
      },
      getText: (user) => getObjectStatus(user.status)?.label || user.status,
    },

    {
      label: '',
      value: 'quick_view',
      isIconCol: true,
      render(user) {
        return (
          <UserNameWithDrawerButton
            editable
            userId={user.id}
            label={<QuickViewButton />}
          />
        );
      },
    },
  ];

  const isPersonInactive = (user) => {
    return user?.status === OBJECT_STATUS.DEACTIVATED.key || false;
  };

  return (
    <AdvancedDataTable
      headers={headers}
      refreshKey={refreshKey}
      title={'Manage Users'}
      dataKey={'USER'}
      data={data}
      dataQueryFunc={runUserQuery}
      filterOptions={filterOptions}
      filterBy={persistentFilter}
      updateFilterField={updateFilterField}
      defaultSort={'full_name'}
      actionButtons={addNewUserButton}
      highlightFunc={isPersonInactive}
    />
  );
};

UserListView.propTypes = {
  addNewUserButton: PropTypes.node,
};

export default UserListView;
